import { CsvError, parse } from 'csv-parse/browser/esm';
import { Options as CSVOptions, stringify } from 'csv-stringify';
import * as XLSX from 'xlsx';

interface SpreadsheetFunctions {
  parse: (blob: Blob) => Promise<Record<string, string>[]>;
  stringify: <T = Record<string, object>>(
    records: Array<T>,
    columnOrder: string[] | { key: string; hide: boolean }[]
  ) => Promise<Blob>;
}

export enum SPREADSHEET_MIME_TYPES {
  XLSX = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  CSV = 'text/csv',
}

/**
 *
 * @param {Blob} blob
 * @return {Promise<string | ArrayBuffer>}
 */
export async function readBlob(
  blob: Blob,
  { binary = false } = {}
): Promise<string | ArrayBuffer | null | undefined> {
  const reader = new FileReader();

  return new Promise((resolve) => {
    reader.addEventListener('load', (e) => resolve(e.target?.result));
    // TODO: Read as buffer, support binary files like XLSX
    if (!binary) {
      reader.readAsText(blob);
    } else {
      reader.readAsArrayBuffer(blob);
    }
  });
}

/**
 *
 * @param {Blob} blob
 * @return {Promise<Record<string, string>[]>}
 */
export async function parseSpreadsheetBlobCSV<T = Record<string, string>[]>(
  blob: Blob
): Promise<T[]> {
  const textContent = (await readBlob(blob)) as string;
  return new Promise((resolve, reject) => {
    if (!textContent) {
      reject(new Error('Text content is empty'));
    }
    try {
      parse(
        textContent,
        { columns: true },
        (err: CsvError | undefined, records: T[]) => {
          if (err) {
            reject(err);
          } else {
            resolve(records);
          }
        }
      );
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      reject(new Error('There was an error parsing the file: ' + message));
    }
  });
}

export async function parseSpreadsheetBlobXLSX(
  blob: Blob
): Promise<Record<string, string>[]> {
  const binaryContent = await readBlob(blob, { binary: true });

  const workbook = XLSX.read(binaryContent, { type: 'array' });
  const worksheet = workbook.Sheets['data'];
  return XLSX.utils.sheet_to_json(worksheet);
}

export async function stringifyToSpreadsheetCSV<T = Record<string, object>>(
  records: Array<T>,
  columnOrder: CSVOptions['columns']
): Promise<Blob> {
  const csvContent: BlobPart = await new Promise((resolve, reject) =>
    stringify(
      records,
      {
        header: true,
        columns: columnOrder,
      },
      (err, output) => {
        if (err) {
          reject(err);
        } else {
          resolve(output);
        }
      }
    )
  );

  return new Blob([csvContent], { type: SPREADSHEET_MIME_TYPES.CSV });
}

export async function stringifyToSpreadsheetXLSX<T = Record<string, object>>(
  records: Array<T>,
  columnOrder: string[] | { key: string; hide: boolean }[]
) {
  const worksheet = XLSX.utils.json_to_sheet(records, {
    header: columnOrder.map(
      (c: string | { key: string; hide: boolean }): string =>
        typeof c !== 'string' ? c.key : c
    ),
  });
  worksheet['!cols'] = columnOrder.map((column) => ({
    hidden: typeof column === 'string' ? false : Boolean(column.hide),
    width: 20,
  }));

  const workbook = { Sheets: { data: worksheet }, SheetNames: ['data'] };
  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  return new Blob([excelBuffer], { type: SPREADSHEET_MIME_TYPES.XLSX });
}

const mimeTypeToSpreasheetFns: Record<
  SPREADSHEET_MIME_TYPES,
  SpreadsheetFunctions
> = {
  [SPREADSHEET_MIME_TYPES.CSV]: {
    parse: parseSpreadsheetBlobCSV,
    stringify: stringifyToSpreadsheetCSV,
  },
  [SPREADSHEET_MIME_TYPES.XLSX]: {
    stringify: stringifyToSpreadsheetXLSX,
    parse: parseSpreadsheetBlobXLSX,
  },
};

/**
 *
 * @param {File} file
 * @return {Promise<Record<string, string>[]>}
 */
export async function parseSpreadsheetFromFile(
  file: File
): Promise<Record<string, string>[]> {
  const getRecordsFromSpreadsheetBlob =
    mimeTypeToSpreasheetFns[file.type as SPREADSHEET_MIME_TYPES]?.parse;

  if (!getRecordsFromSpreadsheetBlob) {
    throw new Error(`Spreadsheet MIME type not supported: ${file.type}`);
  }
  return await getRecordsFromSpreadsheetBlob(file);
}

export async function stringifyToSpreadsheet<T = Record<string, object>>(
  records: Array<T>,
  columnOrder: string[] | { key: string; hide: boolean }[],
  type: SPREADSHEET_MIME_TYPES = SPREADSHEET_MIME_TYPES.XLSX
) {
  const stringifyToSpreadsheetFn = mimeTypeToSpreasheetFns[type]?.stringify;
  if (!stringifyToSpreadsheetFn) {
    throw new Error(`Spreadsheet MIME type not supported: ${type}`);
  }
  return await stringifyToSpreadsheetFn(records, columnOrder);
}

export function downloadBlob(blob: Blob, fileName: string): void {
  const downloadUrl = URL.createObjectURL(blob);
  const anchorElement = document.createElement('a');
  anchorElement.href = downloadUrl;
  anchorElement.download = fileName;
  anchorElement.target = '_blank';
  anchorElement.click();

  setTimeout(() => URL.revokeObjectURL(downloadUrl), 2000);
}
